/**
 * RealtimeStats - Real-time statistics display with WebSocket updates
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAdminWebSocket } from '@/hooks/useWebSocket';
import { Users, CheckCircle, Activity, TrendingUp, Wifi, WifiOff } from 'lucide-react';

// StatCard component defined outside to prevent re-renders
const StatCard = ({ icon: Icon, title, value, trend, color }) => (
  <Card className={`border-l-4 ${color}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <Icon className="h-8 w-8 text-muted-foreground opacity-50" />
      </div>
      {trend && (
        <div className="flex items-center mt-2 text-xs text-green-600">
          <TrendingUp className="h-3 w-3 mr-1" />
          {trend}
        </div>
      )}
    </CardContent>
  </Card>
);

const RealtimeStats = ({ eventId, initialStats = {} }) => {
  const [stats, setStats] = useState({
    total_delegates: initialStats.total_delegates || 0,
    checked_in: initialStats.checked_in || 0,
    active_sessions: initialStats.active_sessions || 0,
    live_viewers: initialStats.live_viewers || 0,
  });
  
  const [recentActivity, setRecentActivity] = useState([]);

  const addActivity = (action, detail) => {
    setRecentActivity(prev => [
      { id: Date.now(), action, detail, time: new Date().toLocaleTimeString() },
      ...prev.slice(0, 9),
    ]);
  };

  const { isConnected } = useAdminWebSocket(eventId, {
    onMessage: (msg) => {
      switch (msg.type) {
        case 'delegate.registered':
          setStats(prev => ({ ...prev, total_delegates: prev.total_delegates + 1 }));
          addActivity('New registration', msg.payload?.name || 'Delegate');
          break;
        case 'delegate.checked_in':
          setStats(prev => ({ ...prev, checked_in: prev.checked_in + 1 }));
          addActivity('Check-in', msg.payload?.name || 'Delegate');
          break;
        case 'session.started':
          setStats(prev => ({ ...prev, active_sessions: prev.active_sessions + 1 }));
          addActivity('Session started', msg.payload?.title || 'Session');
          break;
        case 'session.ended':
          setStats(prev => ({ ...prev, active_sessions: Math.max(0, prev.active_sessions - 1) }));
          addActivity('Session ended', msg.payload?.title || 'Session');
          break;
        case 'stage.live':
          addActivity('Stage went live', msg.payload?.name || 'Stage');
          break;
        default:
          break;
      }
    },
    onConnect: () => console.log('Connected to real-time updates'),
    onError: (err) => console.error('WebSocket error:', err),
  });

  return (
    <div className="space-y-4" data-testid="realtime-stats">
      {/* Connection Status */}
      <div className="flex items-center gap-2 mb-4">
        {isConnected ? (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Wifi className="h-3 w-3 mr-1" />
            Live Updates Active
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <WifiOff className="h-3 w-3 mr-1" />
            Connecting...
          </Badge>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          title="Total Delegates"
          value={stats.total_delegates}
          color="border-l-blue-500"
        />
        <StatCard
          icon={CheckCircle}
          title="Checked In"
          value={stats.checked_in}
          color="border-l-green-500"
        />
        <StatCard
          icon={Activity}
          title="Active Sessions"
          value={stats.active_sessions}
          color="border-l-purple-500"
        />
        <StatCard
          icon={Users}
          title="Live Viewers"
          value={stats.live_viewers}
          color="border-l-orange-500"
        />
      </div>

      {/* Recent Activity Feed */}
      {recentActivity.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentActivity.map((activity) => (
                <div 
                  key={activity.id} 
                  className="flex items-center justify-between py-1 border-b border-muted last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-sm">{activity.action}</span>
                    <span className="text-sm text-muted-foreground">{activity.detail}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{activity.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RealtimeStats;
